/**
 * @file: CustomIssueManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * @file: SiteInspectionManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import { InventoryQuery } from '@euler/functions/useInventory';
import { InspectionSiteInfo, VehicleInspectionFlow } from '@euler/model';
import {
  CommonTaskStatus,
  InspectionTemplateSceneType,
  SiteInspectionType,
} from '@euler/model/enum';
import {
  CustomIssue,
  CustomIssueCommitPayload,
  CustomIssueItemStaged,
  Inspection,
  SiteCustomIssue,
  SiteInspection,
  VersionedResponse,
} from '@euler/model/task-detail';
import { isNotNull } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import produce, { Immutable, freeze } from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { nanoid } from 'nanoid/non-secure';
import { clone } from 'ramda';
import { BehaviorSubject } from 'rxjs';
import {
  MediaUploadTask,
  MediaUploadTaskEvent,
} from '../MediaFileUploadController';
import { IInspectionManager, TaskFileUploadRequestContext } from '../types';
import { customIssueToStaged, mediaStagedToCommitPayload } from '../util';
import { InspectionManagerBase } from './InspectionManagerBase';

export type CustomIssueInspectionParams = {
  flowId: string;
  site?: InspectionSiteInfo;
  inspectionType: SiteInspectionType;
  customIssue?: CustomIssueItemStaged;
};

export class CustomIssueInspectionManager<
  T extends Inspection,
> extends InspectionManagerBase<T> {
  // represents staged commit payload for site inspection
  readonly staged$: BehaviorSubject<CustomIssueItemStaged>;
  readonly removeFileUploadListener: () => void;

  constructor(
    readonly inspectionManager: IInspectionManager<T>,
    readonly params: Readonly<CustomIssueInspectionParams>,
  ) {
    super(inspectionManager.category, inspectionManager.taskManager);

    this.staged$ = new BehaviorSubject(freeze(this.toStaged()));

    this.removeFileUploadListener =
      this.taskManager.uploadController.addListener((e, task) => {
        this.onFileUploadEvent(e, task as any);
      });
  }

  reset() {
    const staged = this.toStaged();
    this.staged$.next(staged);
  }

  dispose() {
    this.removeFileUploadListener();
  }

  removeMedia(key: string) {
    this.taskManager.uploadController.abort(key);
    this.produce((item, original) => {
      const k = original.medias.findIndex(x => x._key === key);
      if (k < 0) {
        console.error(new Error('Cannot find target media, this must be bug'));
        return;
      }
      item.medias.splice(k, 1);
    });
  }

  findMedia(key: string) {
    const current = this.staged$.getValue();
    return current.medias.find(x => x._key === key);
  }

  async deleteCustomIssue() {
    const staged = this.staged$.getValue();
    if (staged.id == null || staged.siteId != null) {
      console.warn(
        'deleteCustomIssue is only expected to be called for existing custom site issue',
      );
      return;
    }
    const res = await this.taskService.deleteCustomIssue(
      this.taskNo,
      staged.id,
    );
    this.handleVersionedResponse(res, (_, { inspection }) => {
      const index = inspection.customIssues?.findIndex(x => x.id === staged.id);
      if (index != null && index >= 0) {
        inspection.customIssues?.splice(index, 1);
      }
    });
  }

  /**
   * Commit the inspected results.
   */
  async commit(
    targetFlow: VehicleInspectionFlow,
    inventoryQuery: InventoryQuery,
  ) {
    const payload = this.getCommitPayload(targetFlow, inventoryQuery);
    if (!payload) {
      throw new Error('Cannot commit invalid data');
    }

    const res = await this.taskService.saveCustomIssue(this.taskNo, payload);

    // if the custom issue is associated with an existing site, try to locate
    // target site inspection and load it if not exists.
    let siteInspection: SiteInspection | undefined = undefined;
    if (res.response.siteId != null) {
      const siteId = res.response.siteId;
      const targetInspection = this.inspectionManager.getTargetInspection(
        this.cachedDetail,
      );
      const index = targetInspection.sites.findIndex(x => x.siteId === siteId);
      if (index < 0) {
        const resp = await this.taskService.getSiteInspectionDetail(
          this.taskNo,
          siteId,
        );
        if (resp.response != null) {
          siteInspection = resp.response;
        }
      }
    }

    this.handleVersionedResponse(
      res,
      (_, { inspection, original, response }) => {
        this.beginApplyCommitResult(
          response,
          inspection,
          original,
          res,
          siteInspection,
        );
      },
    );
    this.endApplyCommitResult(res.response);
  }

  beginApplyCommitResult(
    response: CustomIssue,
    inspection: T,
    original: T,
    res: VersionedResponse<CustomIssue>,
    serverSiteInspection: SiteInspection | undefined,
  ) {
    let handledBySiteInspection = false;
    if (response.siteId != null) {
      // try to update the inspected site information.
      const k = original.sites.findIndex(x => x.siteId === response.siteId);
      if (k >= 0) {
        // update existing defective level if necessary
        const existing = original.sites[k];
        if (existing.abnormalLevel < response.abnormalLevel) {
          inspection.sites[k].abnormalLevel = response.abnormalLevel;
        }
        if (existing.severityLevel < response.severityLevel) {
          inspection.sites[k].severityLevel = response.severityLevel;
        }

        const siteInspection = inspection.sites[k];

        // add the custom issue to the target site inspction
        const index = siteInspection.customIssues.findIndex(
          x => x.id === response.id,
        );
        if (index < 0) {
          siteInspection.customIssues.push(response as SiteCustomIssue);
        } else {
          siteInspection.customIssues[index] = response as SiteCustomIssue;
        }

        handledBySiteInspection = true;
      } else if (serverSiteInspection != null) {
        // insert the new site inspection
        inspection.sites.push(serverSiteInspection);
        handledBySiteInspection = true;
      }
    }

    if (!handledBySiteInspection) {
      // otherwise, add/update the custom issue to the inspection object if necessary
      const index = original.customIssues?.findIndex(x => x.id === response.id);
      if (index == null || index < 0) {
        if (!inspection.customIssues) {
          inspection.customIssues = [response];
        } else {
          inspection.customIssues.push(response);
        }
      } else {
        inspection.customIssues![index] = response;
      }
    }

    // update inspection status.
    if (inspection.status !== CommonTaskStatus.InProgress) {
      inspection.status = CommonTaskStatus.InProgress;
    }

    // try to update the pre-inspection flow status.
    const inspectionType = response.inspectionType;
    if (
      inspectionType === SiteInspectionType.Dashboard ||
      inspectionType === SiteInspectionType.Facade
    ) {
      const sceneType =
        inspectionType === SiteInspectionType.Facade
          ? InspectionTemplateSceneType.Facade
          : InspectionTemplateSceneType.Dashboard;
      const k = original.flows.findIndex(
        x => x.template.sceneType === sceneType,
      );
      const flow = inspection.flows[k];
      if (flow && flow.status === 'pending') {
        flow.status = 'inprogress';
        flow.updatedAt = dateFromValue(res.timestamp);
      }
    }
  }

  endApplyCommitResult(response: CustomIssue) {
    const nextStaged = customIssueToStaged(response);
    nextStaged.relatedFaultLikeSubjects =
      this.cachedDetail.basicInfo.orderDetail?.serviceSubjects
        ?.filter(isFaultLikeServiceSubject)
        ?.map(subject => ({
          name: subject.name,
          checked: subject.relatedInspections?.some(
            x =>
              x.type === 'custom-issue' &&
              ((this.params.site?.code != null &&
                x.siteCode === this.params.site.code) ||
                (this.params.site?.code == null &&
                  x.siteName === response.siteName)) &&
              (x.itemName ?? '') === (response.itemName ?? ''),
          ),
        }));
    this.staged$.next(nextStaged);
  }

  isValidForCommit() {
    const staged = this.staged$.getValue();
    if (this.params.site) {
      // item name is required for site custom item issue.
      if (!staged.itemName?.trim()) {
        return false;
      }
    } else {
      // site name is required for custom site issue.
      if (!staged.siteName?.trim()) {
        return false;
      }
    }
    if (
      !staged.inspectionResult?.trim() ||
      !staged.maintenanceAdvice?.trim() ||
      staged.abnormalLevel == null ||
      staged.severityLevel == null
    ) {
      return false;
    }
    return true;
  }

  getCommitPayload(
    targetFlow: VehicleInspectionFlow,
    inventoryQuery: InventoryQuery,
  ): CustomIssueCommitPayload | undefined {
    if (!this.isValidForCommit()) {
      return undefined;
    }

    const staged = this.staged$.getValue();

    let siteId = this.params.site?.id;
    const siteName = this.params.site
      ? this.params.site.name
      : staged.siteName!;

    if (siteId == null) {
      // try to the locate the target site by name.
      const flowSiteIds = targetFlow.template.conf.categories.flatMap(c =>
        c.groups.flatMap(g =>
          g.siteIds.filter(
            x =>
              g.defaultHiddenSiteIds == null ||
              !g.defaultHiddenSiteIds.includes(x),
          ),
        ),
      );
      for (const flowSiteId of flowSiteIds) {
        const site = inventoryQuery.getSiteById(flowSiteId);
        if (site != null && site.name === siteName) {
          siteId = flowSiteId;
          break;
        }
      }
    }

    const payload: CustomIssueCommitPayload = {
      id: staged.id,
      flowId: this.params.flowId,
      inspectionType: staged.inspectionType,
      siteId,
      siteName,
      itemId: staged.itemId,
      itemName: staged.itemName,
      defectiveClass: staged.defectiveClass,
      description: staged.description,
      abnormalLevel: staged.abnormalLevel!,
      severityLevel: staged.severityLevel!,
      inspectionResult: staged.inspectionResult!,
      maintenanceAdvice: staged.maintenanceAdvice!,
      referenceState: staged.referenceState,
      siteInfluenceFactor: staged.siteInfluenceFactor,
      itemInfluenceFactor: staged.itemInfluenceFactor,
      medias: staged.medias.map(mediaStagedToCommitPayload).filter(isNotNull),
      relatedFaultLikeSubjects: staged.relatedFaultLikeSubjects
        ?.filter(x => x.checked)
        .map(x => x.name),
    };

    return payload;
  }

  toStaged(): CustomIssueItemStaged {
    return this.params.customIssue
      ? clone(this.params.customIssue)
      : {
          _key: nanoid(),
          inspectionType: this.params.inspectionType,
          medias: [],
          relatedFaultLikeSubjects:
            this.cachedDetail.basicInfo.orderDetail?.serviceSubjects
              ?.filter(isFaultLikeServiceSubject)
              ?.map(subject => ({
                name: subject.name,
                checked: false,
              })),
        };
  }

  produce(
    recipe: (
      draft: WritableDraft<CustomIssueItemStaged>,
      original: CustomIssueItemStaged,
    ) => void,
  ) {
    const current = freeze(this.staged$.getValue());
    const nextState = produce(current, draft => {
      recipe(draft, current);
      draft.dirty = true;
    });
    this.staged$.next(nextState);
  }

  private onFileUploadEvent(
    e: MediaUploadTaskEvent,
    task: Immutable<MediaUploadTask<TaskFileUploadRequestContext>>,
  ) {
    const request = task.request;
    const context = request.context;

    // ignore unwanted event that is not specific to me.
    if (context == null || context.type !== 'custom-issue-inspection') {
      return;
    }

    if (e.type === 'queued') {
      // add a new media state when queued.
      this.produce((draft, original) => {
        if (context.key) {
          const index = original.medias.findIndex(x => x._key === context.key);
          if (index >= 0) {
            const media = draft.medias[index];
            media.annotationMetadata = context.annotationMetadata as any;
            media._source = {
              type: 'local',
              localFileUri: request.localFileUri,
              contentType: request.contentType,
              uploadParameters: request.parameters,
              state: { status: 'queued' },
            };
            return;
          }
        }

        draft.medias.push({
          type: request.contentType,
          annotationMetadata: context.annotationMetadata as any,
          _key: context.key ?? task.key,
          _timestamp: Date.now(),
          _source: {
            type: 'local',
            localFileUri: request.localFileUri,
            contentType: request.contentType,
            uploadParameters: request.parameters,
            state: { status: 'queued' },
          },
        });
      });
    } else {
      // update the existing media state.
      this.produce((draft, original) => {
        // use original item to speed up searching
        const index = original.medias.findIndex(
          x => x._key === (context.key ?? task.key),
        );
        if (index < 0) return;
        const media = draft.medias[index];
        if (media._source.type !== 'local') return;
        switch (e.type) {
          case 'compressing': {
            media._source.state = {
              status: 'compressing',
              progress: e.progress,
            };
            return;
          }
          case 'begin': {
            media._source.state = { status: 'uploading', progress: 0 };
            return;
          }
          case 'progress': {
            media._source.state = {
              status: 'uploading',
              progress: e.progress,
            };
            return;
          }
          case 'error': {
            media._source.state = { status: 'error', error: e.error };
            return;
          }
          case 'abort': {
            if (task.status !== 'aborted') {
              media._source.state = {
                status: 'error',
                error: e.error ?? new Error('File uploading has aborted'),
              };
            }
            return;
          }
          case 'finished': {
            const cover = e.result.extra?.cover;
            media.coverUrl = cover != null && cover !== '' ? cover : undefined;
            media._source = {
              type: 'remote',
              url: e.result.url,
              cid: e.result.name,
            };
            return;
          }
          default:
            return;
        }
      });
    }
  }
}
